import { getDefaultBrushAttribute, getDefaultBrushPoint } from '@meitu/saas-editor-core/es/layers/brush-layer';
import {
    getDefaultMarkupAnchor,
    getDefaultMarkupAttribute,
    getDefaultMarkupLabel,
    getDefaultMarkupPoint,
} from '@meitu/saas-editor-core/es/layers/markup-layer';
import { loadAssets } from '@meitu/saas-editor-core/es/utils/file-kit';
import {
    BrushDasharrayType,
    BrushLinecapType,
    BrushPointType,
    BrushType,
    FuncListType,
    MarkupDasharrayType,
    MarkupLabelType,
    MarkupLinecapType,
    MarkupPointType,
    MarkupType,
} from '@meitu/saas-editor-core/es/version-types';
import { getBrushPointListMaterialConfig, getMarkupPointListMaterialConfig } from '@/editor/point-config';
import { getMaterialAssets } from '@/i18n/assets';
import { isIntl } from '@/react-process-env';
import { isInBrushTest } from '@/utils/ab-test';

export enum BrushTagType {
    all = 'all',
    line = 'line',
    curve = 'curve',
    shape = 'shape',
    markup = 'markup',
}

const markupText = isIntl ? 'x-design' : 'meitu';
export const brushList = () => {
    return {
        [BrushTagType.line]: [
            {
                config: {
                    id: 'line-1',
                },
                data: {
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 9,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'line-4',
                },
                data: {
                    brushType: BrushType.Line,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 6,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                            width: 10,
                            linecap: BrushLinecapType.Square,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'line-5',
                },
                data: {
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ed294fff',
                            width: 14,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Dot,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'line-6',
                    bg: '#DEE1E8',
                },
                data: {
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffff22ff',
                            width: 9,
                            linecap: BrushLinecapType.Square,
                            dasharray: BrushDasharrayType.Square,
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'arrow-1',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 12,
                            linecap: BrushLinecapType.Round,
                        },
                    ],
                    startPoint: getDefaultBrushPoint(),
                    endPoint: {
                        type: BrushPointType.HalfArrow,
                        ...getBrushPointListMaterialConfig()[BrushPointType.HalfArrow],
                    },
                },
            },
            {
                config: {
                    id: 'arrow-2',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#20d342ff',
                            width: 10,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Dashed,
                        },
                    ],
                    startPoint: {
                        type: BrushPointType.StraightArrow,
                        ...getBrushPointListMaterialConfig()[BrushPointType.StraightArrow],
                    },
                    endPoint: {
                        type: BrushPointType.StraightArrow,
                        ...getBrushPointListMaterialConfig()[BrushPointType.StraightArrow],
                    },
                },
            },

            {
                config: {
                    id: 'arrow-6',
                    bg: '#DEE1E8',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                            width: 8,
                            linecap: BrushLinecapType.Square,
                        },
                    ],
                    startPoint: {
                        type: BrushPointType.SolidCircle,
                        ...getBrushPointListMaterialConfig()[BrushPointType.SolidCircle],
                    },
                    endPoint: getDefaultBrushPoint(),
                },
            },
            {
                config: {
                    id: 'arrow-5',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#66b1f2ff',
                            width: 10,
                            linecap: BrushLinecapType.Square,
                        },
                    ],
                    startPoint: {
                        type: BrushPointType.VerticalShape,
                        ...getBrushPointListMaterialConfig()[BrushPointType.VerticalShape],
                    },
                    endPoint: {
                        type: BrushPointType.VerticalShape,
                        ...getBrushPointListMaterialConfig()[BrushPointType.VerticalShape],
                    },
                },
            },
            {
                config: {
                    id: 'arrow-3',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffeb57ff',
                            width: 12,
                            linecap: BrushLinecapType.Square,
                        },
                    ],
                    startPoint: getDefaultBrushPoint(),
                    endPoint: {
                        type: BrushPointType.CurvedArrow,
                        ...getBrushPointListMaterialConfig()[BrushPointType.CurvedArrow],
                    },
                },
            },
        ],
        [BrushTagType.curve]: [
            {
                config: {
                    id: 'curve-1',
                    bg: '#DEE1E8',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                            width: 20,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-2',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 5,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffff66',
                            width: 12,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-3',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#fc5672ff',
                            width: 20,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-4',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#ffffffff',
                        width: 6,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#08a5fbff',
                            width: 15,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-5',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 12,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Dashed,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-6',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 6,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                            width: 20,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-7',
                    bg: '#DEE1E8',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#f8ff71b2',
                            width: 24,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-8',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#5b34c6ff',
                        width: 6,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#37099500',
                            width: 20,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-9',
                },
                data: {
                    __vipFuncType: FuncListType.brush,
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#f9df58ff',
                            width: 24,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#ee79a6ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#4866e4ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#41ca75ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
        ],
        [BrushTagType.shape]: [
            {
                config: {
                    id: 'rect-1',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                        },
                    ],
                    radius: 0,
                },
            },
            {
                config: {
                    id: 'rect-2',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#ff5757ff',
                        width: 15,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Dot,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ff575700',
                        },
                    ],
                    radius: 0,
                },
            },
            {
                config: {
                    id: 'rect-3',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 10,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                        },
                    ],
                    radius: 16,
                },
            },
            {
                config: {
                    id: 'rect-4',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 8,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffff00',
                        },
                    ],
                    radius: 0,
                },
            },

            {
                config: {
                    id: 'rect-5',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: {
                                colors: [
                                    { color: '#5fedd5ff', value: 0 },
                                    { color: '#affb7fff', value: 1 },
                                ],
                                angle: 90,
                            },
                        },
                    ],
                    radius: 0,
                },
            },

            {
                config: {
                    id: 'rect-6',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#4c76fbff',
                        width: 10,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffE5',
                        },
                    ],
                    radius: 12,
                },
            },
            {
                config: {
                    id: 'ellipse-1',
                },
                data: {
                    brushType: BrushType.Ellipse,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#ff55b5ff',
                        width: 10,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#00000000',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'ellipse-2',
                },
                data: {
                    brushType: BrushType.Ellipse,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#31cdddff',
                        width: 10,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ddf96eff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-triangle',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.triangle,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#20d342ff',
                        width: 12,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#00000000',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-heart',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.heart,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ff5757ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-star',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.star,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffeb57ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-gon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.gon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#31cdddff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-label',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.label,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#111111ff',
                        width: 12,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#6cdd6cff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-semicircle',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.semicircle,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#111111ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-bubble',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.bubble,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#111111ff',
                        width: 10,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#bbe0ffff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-cloud',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.polygon.cloud,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#bbe0ffff',
                        },
                    ],
                },
            },
        ],
        [BrushTagType.markup]: [
            {
                config: {
                    id: 'markup-1',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-2',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: {
                        type: MarkupPointType.SharpTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                    },
                    endPoint: {
                        type: MarkupPointType.SharpTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-3',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 22,
                        color: '#ffffffff',
                        background: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-4',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                    endPoint: {
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-5',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.HollowRect,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.HollowRect],
                    },
                    endPoint: {
                        type: MarkupPointType.HollowRect,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.HollowRect],
                    },
                },
            },
            {
                config: {
                    id: 'markup-6',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Round,
                        dasharray: MarkupDasharrayType.Dot,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.StraightArrow,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.StraightArrow],
                    },
                    endPoint: {
                        type: MarkupPointType.StraightArrow,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.StraightArrow],
                    },
                },
            },
            {
                config: {
                    id: 'markup-9',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        color: '#000000ff',
                        width: 3,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Dot,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#ffffffff',
                        background: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                    },
                    endPoint: {
                        ...getDefaultMarkupPoint(),
                    },
                },
            },
            {
                config: {
                    id: 'markup-10',
                    bg: '#DEE1E8',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        color: '#ffffffff',
                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#ffffffff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                    endPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-7',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        color: '#d61212ff',
                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#d61212ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-8',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        color: '#4c76fbff',
                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#4c76fbff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidTriangle],
                    },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
        ],
    };
};

/** AB实验画笔列表 */
export const abBrushList = () => {
    return {
        [BrushTagType.line]: [
            {
                config: {
                    id: 'line-1',
                },
                data: {
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 6,
                            linecap: BrushLinecapType.Round,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'line-2',
                },
                data: {
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 5,
                            dasharray: BrushDasharrayType.Square,
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'arrow-1',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 5,
                        },
                    ],
                    startPoint: {
                        type: BrushPointType.VerticalShape,
                        ...getBrushPointListMaterialConfig()[BrushPointType.VerticalShape],
                    },
                    endPoint: {
                        type: BrushPointType.VerticalShape,
                        ...getBrushPointListMaterialConfig()[BrushPointType.VerticalShape],
                    },
                },
            },
            {
                config: {
                    id: 'arrow-2',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 6,
                            dasharray: BrushDasharrayType.Square,
                        },
                    ],
                    startPoint: {
                        type: BrushPointType.SolidTriangle,
                        ...getBrushPointListMaterialConfig()[BrushPointType.SolidTriangle],
                    },
                    endPoint: {
                        type: BrushPointType.SolidTriangle,
                        ...getBrushPointListMaterialConfig()[BrushPointType.SolidTriangle],
                    },
                },
            },

            {
                config: {
                    id: 'arrow-3',
                },
                data: {
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 5,
                        },
                    ],
                    startPoint: getDefaultBrushPoint(),
                    endPoint: {
                        type: BrushPointType.StraightArrow,
                        ...getBrushPointListMaterialConfig()[BrushPointType.StraightArrow],
                    },
                },
            },
        ],
        [BrushTagType.curve]: [
            {
                config: {
                    id: 'curve-1',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 10,
                            linecap: BrushLinecapType.Round,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-2',
                },
                data: {
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#000000ff',
                            width: 9,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Dashed,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-3',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 24,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffff66',
                            width: 12,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-4',
                },
                data: {
                    brushType: BrushType.Curve,
                    stroke: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 6,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#ffffffff',
                            width: 20,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'curve-5',
                },
                data: {
                    __vipFuncType: FuncListType.brush,
                    brushType: BrushType.Curve,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#f9df58ff',
                            width: 24,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#ee79a6ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#4866e4ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                        {
                            color: '#41ca75ff',
                            width: 24,
                            offsetX: 8,
                            offsetY: 8,
                            linecap: BrushLinecapType.Round,
                            dasharray: BrushDasharrayType.Solid,
                        },
                    ],
                },
            },
        ],
        [BrushTagType.shape]: [
            {
                config: {
                    id: 'polygon-rect-1',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                    radius: 0,
                },
            },
            {
                config: {
                    id: 'polygon-rect-2',
                },
                data: {
                    brushType: BrushType.Rect,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                    radius: 100,
                },
            },
            {
                config: {
                    id: 'polygon-ellipse',
                },
                data: {
                    brushType: BrushType.Ellipse,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-semicircle',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.semicircle,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-rectangle-octagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.rectangleOctagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-triangle',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.triangle,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-pentagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.pentagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-hexagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.hexagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-diamond',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.diamond,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-right-trapezoid',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.rightTrapezoid,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-isosceles-trapezoid',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.isoscelesTrapezoid,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-arch',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.arch,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-quadrilateral',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.quadrilateral,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-star',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.star,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-octagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.octagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-dodecagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.dodecagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-icosagon',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.icosagon,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-icosegment',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.icosegment,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-trigonad',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.trigonad,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-ban',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.ban,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-flag-1',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.flag1,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-flag-2',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.flag2,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-top-arrow',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.topArrow,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-bottom-arrow',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.bottomArrow,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-left-arrow',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.leftArrow,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-right-arrow',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.rightArrow,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },

            {
                config: {
                    id: 'polygon-double-arrow',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.doubleArrow,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-flag-3',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.flag3,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-bubble-1',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.bubble1,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-bubble-2',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.bubble2,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-heart',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.heart,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-cloud',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.cloud,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-plus',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.plus,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
            {
                config: {
                    id: 'polygon-cross',
                },
                data: {
                    brushType: BrushType.Polygon,
                    url: getMaterialAssets().brush.abPolygon.cross,
                    stroke: getDefaultBrushAttribute(),
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: '#5c6063ff',
                        },
                    ],
                },
            },
        ],
        [BrushTagType.markup]: [
            {
                config: {
                    id: 'markup-1',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-2',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: {
                        type: MarkupPointType.SharpTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                    },
                    endPoint: {
                        type: MarkupPointType.SharpTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SharpTriangle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-3',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 22,
                        color: '#ffffffff',
                        background: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: true,
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-4',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                    endPoint: {
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-5',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Square,
                        dasharray: MarkupDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.HollowRect,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.HollowRect],
                    },
                    endPoint: {
                        type: MarkupPointType.HollowRect,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.HollowRect],
                    },
                },
            },
            {
                config: {
                    id: 'markup-6',
                },
                data: {
                    __drawClick: 2,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Arrow,
                    fill: {
                        ...getDefaultMarkupAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: MarkupLinecapType.Round,
                        dasharray: MarkupDasharrayType.Dot,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        fontSize: 22,
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                        arrowLeft: [12, -12],
                        arrowRight: [12, -12],
                    },
                    hasLine: false,
                    startPoint: {
                        type: MarkupPointType.StraightArrow,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.StraightArrow],
                    },
                    endPoint: {
                        type: MarkupPointType.StraightArrow,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.StraightArrow],
                    },
                },
            },
            {
                config: {
                    id: 'markup-9',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 3,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Dot,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#ffffffff',
                        background: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                    },
                    endPoint: {
                        ...getDefaultMarkupPoint(),
                    },
                },
            },
            {
                config: {
                    id: 'markup-10',
                    bg: '#DEE1E8',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        ...getDefaultBrushAttribute(),
                        color: '#ffffffff',
                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Square,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#ffffffff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                    endPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidCircle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidCircle],
                    },
                },
            },
            {
                config: {
                    id: 'markup-7',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',
                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: { ...getDefaultMarkupPoint() },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
            {
                config: {
                    id: 'markup-8',
                },
                data: {
                    __drawClick: 3,
                    __vipFuncType: FuncListType.markup,
                    markupType: MarkupType.Segment,
                    fill: {
                        ...getDefaultBrushAttribute(),
                        color: '#000000ff',

                        width: 2,
                        linecap: BrushLinecapType.Square,
                        dasharray: BrushDasharrayType.Solid,
                    },
                    label: {
                        ...getDefaultMarkupLabel(),
                        type: MarkupLabelType.custom,
                        text: markupText,
                        fontFamily: 'AlibabaPuHuiTi-Medium',
                        fontSize: 25,
                        color: '#000000ff',
                    },
                    anchor: {
                        ...getDefaultMarkupAnchor(),
                    },
                    startPoint: {
                        ...getDefaultMarkupPoint(),
                        type: MarkupPointType.SolidTriangle,
                        ...getMarkupPointListMaterialConfig()[MarkupPointType.SolidTriangle],
                    },
                    endPoint: { ...getDefaultMarkupPoint() },
                },
            },
        ],
    };
};

//不能直接使用，abTest包还没初始化好
// const previewAssets = isInBrushTest()
//     ? loadAssets(import.meta.glob('./assets/ab-preview/*.png', { eager: true }))
//     : loadAssets(import.meta.glob('./assets/preview/*.png', { eager: true }));

let previewAssets: Map<string, string> | null = null;
const previewIntlAssets = loadAssets(import.meta.glob('./assets/preview-intl/*.png', { eager: true }));

function getPreviewAssets() {
    if (!previewAssets) {
        //AB实验区分路径
        previewAssets = isInBrushTest()
            ? loadAssets(import.meta.glob('./assets/ab-preview/*.png', { eager: true }))
            : loadAssets(import.meta.glob('./assets/preview/*.png', { eager: true }));
    }
    return previewAssets;
}

export function getPreviewImage(id: string, width: number, height: number) {
    const previewAssets = getPreviewAssets();
    return (
        <img
            src={isIntl && id.includes('markup') ? previewIntlAssets.get(id) : previewAssets.get(id)}
            width={width}
            height={height}
            alt=""
        />
    );
}

// 埋点 type_id
export const statTypeIdMap: { [key: string]: number } = {
    line: 1101,
    curve: 1099,
    arrow: 1101,
    ellipse: 1100,
    polygon: 1100,
    rect: 1100,
    markup: 1102, //暂定标注笔为1102
};
